import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
} from "../store/features/addressesSlice";
import { Button, EmptyState, Input, Loader, PageHeader } from "../components/ui";
import { MapPinIcon, PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/24/outline";

const AddressForm = ({ isNew, value, onChange, onSave, onCancel }) => (
  <form
    className="bg-slate-900/90 border border-slate-700 p-5 rounded-3xl mb-6 shadow-xl space-y-5"
    onSubmit={(e) => {
      e.preventDefault();
      if (value.trim()) onSave();
    }}
  >
    <h3 className="text-lg font-bold">{isNew ? "Новый адрес" : "Редактировать адрес"}</h3>
    <Input
      label="Адрес доставки"
      autoFocus
      placeholder="Улица, дом, квартира, город"
      autoComplete="street-address"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
    <div className="flex gap-3">
      <Button type="submit" size="lg" className="flex-1" disabled={!value.trim()}>
        Сохранить
      </Button>
      <Button variant="secondary" size="lg" className="flex-1" onClick={onCancel}>
        Отмена
      </Button>
    </div>
  </form>
);

const UserAddressPage = () => {
  const dispatch = useDispatch();
  const { list: addresses, status, error } = useSelector((state) => state.addresses);

  // null — форма закрита, "new" — новий адрес, інакше id адреси, що редагується
  const [editingId, setEditingId] = useState(null);
  const [formAddress, setFormAddress] = useState("");

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  const openForm = (id = "new", address = "") => {
    setEditingId(id);
    setFormAddress(address);
  };

  const closeForm = () => openForm(null);

  const handleSave = async () => {
    if (editingId === "new") {
      await dispatch(addAddress(formAddress));
    } else {
      await dispatch(updateAddress({ id: editingId, address: formAddress }));
    }
    closeForm();
  };

  const handleDelete = (id) => {
    if (window.confirm("Удалить адрес?")) {
      dispatch(deleteAddress(id));
    }
  };

  return (
    <div className="text-white">
      <PageHeader
        title="Мои адреса"
        action={
          <Button size="md" onClick={() => openForm()}>
            <PlusIcon className="w-5 h-5" />
            Добавить
          </Button>
        }
      />

      <main className="px-2 pb-6 max-w-xl mx-auto">
        {status === "loading" && <Loader />}
        {error && (
          <p className="text-red-300 bg-red-500/10 p-4 rounded-2xl mb-4 border border-red-500/30">{error}</p>
        )}

        {editingId && (
          <AddressForm
            isNew={editingId === "new"}
            value={formAddress}
            onChange={setFormAddress}
            onSave={handleSave}
            onCancel={closeForm}
          />
        )}

        {addresses.length === 0 && status === "succeeded" && !editingId && (
          <EmptyState
            icon={MapPinIcon}
            title="У вас нет сохранённых адресов"
            text="Добавьте адрес доставки, чтобы оформлять заказы быстрее — его можно будет выбрать при оформлении заказа."
            action={
              <Button size="lg" block className="max-w-xs" onClick={() => openForm()}>
                Добавить адрес
              </Button>
            }
          />
        )}

        <div className="space-y-4">
          {addresses.map((addr, idx) => (
            <div
              key={addr.id}
              className="bg-slate-900/80 border border-slate-700 rounded-2xl overflow-hidden hover:border-slate-500 transition-colors"
            >
              <div className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-blue-400 shrink-0">
                  <MapPinIcon className="w-6 h-6" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                      Адрес {idx + 1}
                    </span>
                    {idx === 0 && (
                      <span className="px-2 py-0.5 bg-primary/15 text-blue-300 text-[10px] font-bold uppercase tracking-wider rounded-full">
                        Основной
                      </span>
                    )}
                  </div>
                  <p className="text-slate-100 font-medium break-words leading-relaxed">{addr.address}</p>
                </div>
              </div>

              <div className="flex border-t border-slate-700">
                <button
                  onClick={() => openForm(addr.id, addr.address)}
                  className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold text-slate-300 hover:bg-slate-800 transition-colors border-r border-slate-700"
                >
                  <PencilIcon className="w-4 h-4" />
                  Изменить
                </button>
                <button
                  onClick={() => handleDelete(addr.id)}
                  className="flex-1 flex items-center justify-center gap-2 py-4 text-sm font-bold text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default UserAddressPage;
